// Tier 3 / F-33 — First-unread divider.
//
// Renders a thin red horizontal rule with a centered "N unread messages"
// label. Inserted into the message stream just above the first unread
// inbound message. The detection of *which* message is the boundary is
// done by the caller (ChatPage.jsx) — this component just renders the
// presentation.
//
// Design note: the message pane uses `flexDirection: column-reverse` so
// the divider must NOT use direction-aware styling (it's just a rule with
// centered text). Native WhatsApp uses light grey here, but the brief
// asks for the theme error red (`#d32f2f`) so the operator's eye is
// drawn straight to it.

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

const RED = '#d32f2f';

const UnreadDivider = React.forwardRef(({ count }, ref) => {
    if (!count || count <= 0) return null;
    const label = `${count} unread message${count === 1 ? '' : 's'}`;
    return (
        <Box
            ref={ref}
            data-testid="wa-unread-divider"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: '12px 4px',
                // The floating jump button watches for this element via
                // IntersectionObserver — keep `id` stable.
            }}
            id="wa-unread-divider"
        >
            <Box sx={{ flex: 1, height: '1px', backgroundColor: RED, opacity: 0.85 }} />
            <Typography
                component="span"
                sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: RED,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: RED, opacity: 0.85 }} />
        </Box>
    );
});

UnreadDivider.propTypes = {
    count: PropTypes.number,
};

UnreadDivider.defaultProps = {
    count: 0,
};

UnreadDivider.displayName = 'UnreadDivider';

export default UnreadDivider;
